import { Component } from '@theme/component';
import { search, isSearchEnabled } from '@theme/typesense-client';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Typesense-backed results page (search and collection).
 *
 * Takes over the results area when Typesense is enabled: it reads the
 * query/filter/sort state from the URL, queries Typesense, and renders the
 * product grid and facets client-side while keeping the URL in the theme's
 * `filter.v.*` convention so links stay shareable.
 *
 * Collection pages reuse this element with two extra attributes:
 *   - `base-filter`: a `filter_by` clause ANDed into every query
 *     (e.g. `category_ids:=123456`), scoping results to the collection.
 *   - `default-sort`: a `sort_by` expression applied while the shopper hasn't
 *     picked a sort (e.g. `stock_rank:asc`). A sort option whose expression
 *     matches is shown as selected.
 * The host section may server-render grid items and pagination links inside
 * the `grid`/`pagination` refs for SEO/no-JS visitors; the first Typesense
 * response replaces them (and on failure they simply remain).
 *
 * Facet groups, toggle filters and sort options are configured in the theme
 * settings under "Search (Typesense)" and injected via `Theme.search.facetsConfig`
 * / `Theme.search.togglesConfig` / `Theme.search.sortConfig`. A facet whose field
 * is `price` is rendered as a min/max range; all other facet fields must be
 * `facet: true` in the schema. Toggle filters are single on/off switches pinned
 * above the facet groups, each ANDing a raw `filter_by` expression into the query
 * (e.g. `stock_rank:=1` for "ready to ship"), so their fields only need to be
 * filterable — not facetable.
 *
 * A facet field ending in `*` is a hierarchical (drill-down) category menu: the
 * marker stands for the numbered level fields, so `category_lvl*` covers
 * `category_lvl0`…`category_lvl2` (append a digit — `category_lvl*4` — for a
 * deeper tree). Each level indexes the *full* path of the node it names
 * (`category_lvl1` holds `Feeding > Bottles`), which is what lets the tree be
 * rebuilt from the flat counts. Exactly one node is selected at a time, stored
 * under its own level's field so it flows through the URL and `filter_by` like
 * any other checkbox facet.
 */

const PRICE_FIELD = 'price';
/** Checkbox facets listed A–Z instead of by count. */
const ALPHABETICAL_FACETS = new Set(['vendor']);
/** Marks a facet field as a hierarchical menu, optionally with its depth. */
const HIERARCHY_MARKER = /\*(\d*)$/;
/** Default number of levels behind a bare `*` (lvl0, lvl1, lvl2). */
const HIERARCHY_DEPTH = 3;
/** Separates the path segments inside a hierarchy facet value. */
const HIERARCHY_SEPARATOR = ' > ';
/** Child nodes shown per level before the "show more" control. */
const HIERARCHY_VISIBLE = 12;
const ELLIPSIS = '…';
const SEARCH_QUERY = 'q';
const SORT_PARAM = 'sort_by';
const FILTER_PREFIX = 'filter.v.';
const PRICE_MIN_PARAM = 'filter.v.price.gte';
const PRICE_MAX_PARAM = 'filter.v.price.lte';
/** URL value written for an active toggle filter (`filter.v.<key>=1`). */
const TOGGLE_ON = '1';

/**
 * Escapes a value for safe interpolation into HTML.
 * @param {string | number | null | undefined} value
 * @returns {string}
 */
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * @typedef {Object} FacetConfig
 * @property {string} field - The Typesense field, or the level-field prefix when hierarchical.
 * @property {string} label - The group heading.
 * @property {number} depth - Number of levels for a hierarchical facet, 0 otherwise.
 */

/**
 * Parses the facets setting (`field|Label` per line).
 *
 * A `*` suffix on the field marks a hierarchical menu and is stripped from the
 * field, which then names the level prefix: `category_lvl*` -> `category_lvl`
 * with depth 3. A digit after the marker overrides the depth.
 * @param {string} raw
 * @returns {FacetConfig[]}
 */
const parseFacetsConfig = (raw) =>
  (raw || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawField, ...rest] = line.split('|');
      const field = (rawField ?? '').trim();
      const label = (rest.join('|') || field || '').trim();
      const marker = field.match(HIERARCHY_MARKER);

      if (!marker) return { field, label, depth: 0 };
      return {
        field: field.replace(HIERARCHY_MARKER, ''),
        label,
        depth: Number(marker[1]) || HIERARCHY_DEPTH,
      };
    })
    .filter((facet) => facet.field);

/**
 * The numbered level fields of a hierarchical facet (`category_lvl0`, …).
 * @param {FacetConfig} facet
 * @returns {string[]}
 */
const hierarchyLevels = (facet) => Array.from({ length: facet.depth }, (_, level) => `${facet.field}${level}`);

/**
 * The depth of a hierarchy value, derived from its path (`A > B` -> 1).
 * @param {string} path
 * @returns {number}
 */
const pathDepth = (path) => path.split(HIERARCHY_SEPARATOR).length - 1;

/**
 * Builds a windowed list of page numbers with ellipses, e.g. [1, '…', 4, 5, 6, '…', 20].
 * @param {number} current - The current page.
 * @param {number} total - The total number of pages.
 * @returns {Array<number|string>}
 */
const paginationRange = (current, total) => {
  /** @type {Array<number|string>} */
  const pages = [];
  for (let page = 1; page <= total; page++) {
    if (page === 1 || page === total || Math.abs(page - current) <= 1) {
      pages.push(page);
    } else if (pages[pages.length - 1] !== ELLIPSIS) {
      pages.push(ELLIPSIS);
    }
  }
  return pages;
};

/**
 * Parses the sort options setting (`Label|expression` per line).
 * @param {string} raw
 * @returns {Array<{ label: string, expr: string }>}
 */
const parseSortConfig = (raw) =>
  (raw || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, ...rest] = line.split('|');
      return { label: (label ?? '').trim(), expr: rest.join('|').trim() };
    })
    .filter((option) => option.label);

/**
 * Parses the toggle filters setting (`key|Label|filter expression` per line).
 *
 * The expression is everything after the second `|`, so Typesense's `||` (OR)
 * survives, e.g. `ready|Ready to ship|stock_rank:=1 || stock_locations:=\`CES\``.
 * The key is the URL parameter suffix, so it must stay stable when the label
 * changes or is translated.
 * @param {string} raw
 * @returns {Array<{ key: string, label: string, expr: string }>}
 */
const parseTogglesConfig = (raw) =>
  (raw || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [key, label, ...rest] = line.split('|');
      const trimmedKey = (key ?? '').trim();
      return { key: trimmedKey, label: (label ?? '').trim() || trimmedKey, expr: rest.join('|').trim() };
    })
    .filter((toggle) => toggle.key && toggle.expr);

/**
 * @typedef {Object} TypesenseSearchPageRefs
 * @property {HTMLElement} facetList - Container for facet groups.
 * @property {HTMLUListElement} grid - The product grid list.
 * @property {HTMLElement} count - The results count element.
 * @property {HTMLDetailsElement} sortDetails - The sort dropdown wrapper.
 * @property {HTMLElement} sortPanel - The sort options listbox.
 * @property {HTMLElement} sentinel - The infinite-scroll sentinel.
 * @property {HTMLElement} pagination - The numbered pagination container.
 * @property {HTMLElement} empty - The empty/no-results message.
 * @property {HTMLButtonElement} [clearAll] - The clear-all-filters button.
 *
 * @extends {Component<TypesenseSearchPageRefs>}
 */
class TypesenseSearchPage extends Component {
  requiredRefs = ['facetList', 'grid', 'count', 'sortDetails', 'sortPanel', 'sentinel', 'pagination', 'empty'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Guards against overlapping infinite-scroll loads. */
  #loading = false;

  /** @type {{ query: string, sort: string, priceMin: string, priceMax: string, filters: Map<string, Set<string>>, toggles: Set<string>, page: number }} */
  #state = { query: '', sort: '', priceMin: '', priceMax: '', filters: new Map(), toggles: new Set(), page: 1 };

  /** Toggle keys the collection's schema rejected, dropped for this page load. */
  #unsupportedToggles = new Set();

  /**
   * Facet keys the shopper has expanded. Groups start collapsed on page load and
   * are re-rendered on every response, so their open state lives here.
   * @type {Set<string>}
   */
  #openFacets = new Set();

  #found = 0;

  /**
   * Typesense hits the ID bridge returned no card for, across the pages shown so
   * far: products Shopify won't render (draft, unpublished, deleted) that the
   * index still lists. Subtracted from the displayed count.
   */
  #missing = 0;

  /** The server-rendered page title, which carries Shopify's native result count. */
  #initialTitle = document.title;

  /**
   * Per hierarchical facet, the collection's own node on a collection page (see
   * `#findCollectionNode`), and whether the tree is re-rooted below it or just
   * leaves it out; absent when the full tree is showing.
   * @type {Map<string, { path: string, reRoot: boolean }>}
   */
  #hierarchyRoots = new Map();

  get #facets() {
    return parseFacetsConfig(Theme.search?.facetsConfig ?? '');
  }

  /** The configured toggle filters, minus any the collection rejected. */
  get #toggles() {
    return parseTogglesConfig(Theme.search?.togglesConfig ?? '').filter(
      (toggle) => !this.#unsupportedToggles.has(toggle.key)
    );
  }

  get #sortOptions() {
    return parseSortConfig(Theme.search?.sortConfig ?? '');
  }

  /**
   * Facet field names that get `facet_by` counts — everything except the price
   * range, with hierarchical facets expanded to their level fields.
   */
  get #facetFields() {
    return this.#facets.flatMap((facet) => {
      if (facet.depth) return hierarchyLevels(facet);
      return facet.field === PRICE_FIELD ? [] : [facet.field];
    });
  }

  /** The configured hierarchical facets. */
  get #hierarchyFacets() {
    return this.#facets.filter((facet) => facet.depth > 0);
  }

  /** Every level field across all hierarchical facets. */
  get #hierarchyFields() {
    return this.#hierarchyFacets.flatMap(hierarchyLevels);
  }

  get #perPage() {
    return Number(this.getAttribute('per-page')) || 24;
  }

  get #sectionId() {
    return this.getAttribute('section-id') ?? '';
  }

  /** Whether to auto-load on scroll (mirrors the section's "Auto-load products" setting). */
  get #infiniteScroll() {
    return this.getAttribute('infinite-scroll') !== 'false';
  }

  /** A `filter_by` clause ANDed into every query (collection pages: `category_ids:=<id>`). */
  get #baseFilter() {
    return this.getAttribute('base-filter') ?? '';
  }

  /** The `sort_by` expression used while the shopper hasn't picked a sort. */
  get #defaultSort() {
    return this.getAttribute('default-sort') ?? '';
  }

  connectedCallback() {
    super.connectedCallback();
    if (!isSearchEnabled()) {
      this.#revealGrid();
      return;
    }

    this.refs.facetList.addEventListener('change', this.#handleFacetChange);
    this.refs.facetList.addEventListener('click', this.#handleFacetClick);
    this.refs.facetList.addEventListener('keydown', this.#handlePriceKeydown);
    this.refs.facetList.addEventListener('toggle', this.#handleFacetToggle, true);
    this.refs.sortPanel.addEventListener('click', this.#handleSortSelect);
    this.refs.sortDetails.addEventListener('toggle', this.#handleSortToggle);
    this.refs.sortDetails.addEventListener('keydown', this.#handleSortKeydown);
    this.refs.pagination.addEventListener('click', this.#handlePaginationClick);
    this.refs.clearAll?.addEventListener('click', this.#handleClearAll);
    window.addEventListener('popstate', this.#handlePopState);
    document.addEventListener('click', this.#handleDocumentClick);

    if (this.#infiniteScroll) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.#loadNextPage();
        },
        { rootMargin: '600px' }
      );
    }

    this.#readStateFromUrl();
    this.#expandActiveFacets();
    this.#renderSortOptions();
    this.#runSearch({ append: false });
  }

  /**
   * Expands the groups a shared link arrives already filtered by, so the shopper
   * can see (and undo) the filter instead of a collapsed row of bubbles. Seeds
   * `#openFacets` once, on load, so collapsing a group afterwards still sticks.
   */
  #expandActiveFacets() {
    if (this.#state.priceMin || this.#state.priceMax) this.#openFacets.add(PRICE_FIELD);

    for (const [field, values] of this.#state.filters) {
      if (!values.size) continue;
      const owner = this.#facets.find(
        (facet) => facet.field === field || (facet.depth > 0 && hierarchyLevels(facet).includes(field))
      );
      if (owner) this.#openFacets.add(owner.field);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.#observer?.disconnect();
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Reads query/filter/sort state from the current URL.
   */
  #readStateFromUrl() {
    const params = new URL(window.location.href).searchParams;

    this.#state.query = params.get(SEARCH_QUERY) ?? '';
    this.#state.sort = params.get(SORT_PARAM) ?? '';
    this.#state.priceMin = params.get(PRICE_MIN_PARAM) ?? '';
    this.#state.priceMax = params.get(PRICE_MAX_PARAM) ?? '';
    // Infinite scroll always starts at page 1 and accumulates; numbered pages persist in the URL.
    this.#state.page = this.#infiniteScroll ? 1 : Math.max(1, Number(params.get('page')) || 1);

    const filters = new Map();
    for (const field of this.#facetFields) {
      const values = params.getAll(`${FILTER_PREFIX}${field}`).filter(Boolean);
      if (values.length) filters.set(field, new Set(values));
    }

    // A hierarchy selection is a single node: a hand-edited or stale URL naming
    // several levels keeps only the deepest, so the tree can't render a branch
    // that contradicts the filter.
    for (const facet of this.#hierarchyFacets) {
      const levels = hierarchyLevels(facet).filter((field) => filters.get(field)?.size);
      for (const field of levels.slice(0, -1)) filters.delete(field);
    }

    this.#state.filters = filters;

    const toggles = new Set();
    for (const { key } of this.#toggles) {
      if (params.get(`${FILTER_PREFIX}${key}`)) toggles.add(key);
    }
    this.#state.toggles = toggles;
  }

  /**
   * Writes the current state back to the URL.
   * @param {Object} [options] - Write options.
   * @param {boolean} [options.replace] - Replace the history entry instead of
   *   pushing one (for corrections the shopper did not ask for).
   */
  #updateUrl({ replace = false } = {}) {
    const url = new URL(window.location.href);
    url.search = '';
    const params = url.searchParams;

    if (this.#state.query) params.set(SEARCH_QUERY, this.#state.query);
    if (this.#state.sort) params.set(SORT_PARAM, this.#state.sort);
    if (this.#state.priceMin) params.set(PRICE_MIN_PARAM, this.#state.priceMin);
    if (this.#state.priceMax) params.set(PRICE_MAX_PARAM, this.#state.priceMax);

    for (const [field, values] of this.#state.filters) {
      for (const value of values) params.append(`${FILTER_PREFIX}${field}`, value);
    }

    for (const key of this.#state.toggles) params.set(`${FILTER_PREFIX}${key}`, TOGGLE_ON);

    if (!this.#infiniteScroll && this.#state.page > 1) params.set('page', String(this.#state.page));

    if (replace) history.replaceState({}, '', url.toString());
    else history.pushState({}, '', url.toString());
  }

  /**
   * Builds the Typesense `filter_by` expression from the current state.
   * @param {Object} [options] - Build options.
   * @param {boolean} [options.excludeHierarchy] - Omit the hierarchy level clauses,
   *   for the sibling-count search (see `#runSearch`).
   * @returns {string}
   */
  #buildFilterBy({ excludeHierarchy = false } = {}) {
    const parts = [];
    const skip = excludeHierarchy ? new Set(this.#hierarchyFields) : new Set();

    if (this.#state.priceMin) parts.push(`${PRICE_FIELD}:>=${Number(this.#state.priceMin)}`);
    if (this.#state.priceMax) parts.push(`${PRICE_FIELD}:<=${Number(this.#state.priceMax)}`);

    for (const [field, values] of this.#state.filters) {
      if (!values.size || skip.has(field)) continue;
      const list = [...values].map((value) => `\`${value.replace(/`/g, '\\`')}\``).join(',');
      parts.push(`${field}:=[${list}]`);
    }

    // Merchant-authored expressions, parenthesised so an inner `||` can't
    // swallow the clauses they are ANDed with.
    const expressions = new Map(this.#toggles.map((toggle) => [toggle.key, toggle.expr]));
    for (const key of this.#state.toggles) {
      const expression = expressions.get(key);
      if (expression) parts.push(`(${expression})`);
    }

    return parts.join(' && ');
  }

  #newController() {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Resets to page 1, updates the URL and re-queries.
   */
  #resetAndSearch() {
    this.#state.page = 1;
    this.#updateUrl();
    this.#runSearch({ append: false });
  }

  /**
   * Runs a Typesense query and renders the results.
   * @param {{ append: boolean }} options - Whether to append (load more) or replace.
   */
  async #runSearch({ append }) {
    const controller = this.#newController();
    this.#loading = true;

    // With a category node selected, Typesense scopes the level fields' own
    // counts to that branch, so the shopper would lose every sibling. Ask for a
    // second set of counts taken without the hierarchy clauses; with nothing
    // selected the main counts already are that, so skip the extra search.
    const hierarchyActive = this.#hierarchyFields.some((field) => this.#state.filters.get(field)?.size);
    const treeFacetBy = hierarchyActive ? this.#hierarchyFields.join(',') : undefined;

    try {
      const result = await search(this.#state.query, {
        signal: controller.signal,
        page: this.#state.page,
        perPage: this.#perPage,
        filterBy: [this.#baseFilter, this.#buildFilterBy()].filter(Boolean).join(' && ') || undefined,
        sortBy: this.#state.sort || this.#defaultSort || undefined,
        facetBy: this.#facetFields.join(',') || undefined,
        treeFacetBy,
        treeFilterBy: treeFacetBy
          ? [this.#baseFilter, this.#buildFilterBy({ excludeHierarchy: true })].filter(Boolean).join(' && ') || undefined
          : undefined,
      });

      if (controller.signal.aborted) return;

      this.#found = result.found;
      this.#renderMeta(result, append);
      const missing = await this.#renderNativeCards(result.hits, append, controller.signal);
      if (controller.signal.aborted) return;
      this.#missing = (append ? this.#missing : 0) + missing;
      if (this.#missing) this.#renderCount(result.found - this.#missing);
      this.#updatePagination(result);
    } catch (error) {
      if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      // Not `append`: the dropped toggles reset the state to page 1, so the
      // retry replaces the grid instead of appending page 1 beneath it.
      if (this.#dropUnsupportedToggles(error)) {
        await this.#runSearch({ append: false });
        return;
      }
      console.error('Typesense results page query failed.', error);
    } finally {
      if (!controller.signal.aborted) {
        this.#loading = false;
        this.#revealGrid();
      }
    }
  }

  /**
   * Drops active toggle filters the collection can't apply, then reports that a
   * retry is warranted.
   *
   * Typesense rejects a `filter_by` naming a field the collection lacks — an
   * index not yet reindexed with the field, or a typo in the setting. That comes
   * back as a per-search error inside HTTP 200, which the client throws. Leaving
   * it unhandled would show the shopper a toggle that appears to do nothing
   * (the grid keeps whatever it already had), so forget the toggles for this
   * page load and re-query without them. All active toggles are dropped rather
   * than bisected to find the culprit; with a handful of toggles that costs one
   * retry instead of several.
   * @param {unknown} error - The error thrown by the search.
   * @returns {boolean} Whether the caller should retry without the toggles.
   */
  #dropUnsupportedToggles(error) {
    const active = [...this.#state.toggles];
    if (!active.length) return false;
    // Only query-level rejections mean the expression is unusable; network and
    // HTTP failures say nothing about the filter, so leave the state alone.
    if (!(error instanceof Error) || !error.message.startsWith('Typesense search error')) return false;

    console.warn(`Typesense: toggle filter(s) "${active.join('", "')}" are not supported by this collection.`, error);
    for (const key of active) this.#unsupportedToggles.add(key);
    this.#state.toggles = new Set();
    this.#state.page = 1;
    this.#updateUrl({ replace: true });
    return true;
  }

  /**
   * Reveals the grid and pagination, which the host section may hold hidden
   * (via an inline script) until the first Typesense response has replaced the
   * server-rendered content — on failure this shows the SSR fallback instead.
   */
  #revealGrid() {
    this.classList.remove('typesense-results--takeover');
  }

  /**
   * Renders the count and facets from the Typesense response.
   * @param {import('@theme/typesense-client').TypesenseSearchResult} result
   * @param {boolean} append
   */
  #renderMeta(result, append) {
    this.#renderCount(append ? result.found - this.#missing : result.found);
    if (!append) this.#renderFacets(result.facets, result.treeFacets, result.treeFound);
  }

  /**
   * Shows the result count (grid header, tab title) and the empty state.
   * @param {number} total
   */
  #renderCount(total) {
    const { count, empty } = this.refs;
    const shown = Math.max(0, total);

    count.textContent = (Theme.search?.strings.itemCount ?? '').replace('%%COUNT%%', String(shown));
    empty.hidden = shown > 0;
    this.#syncTitleCount(shown);
  }

  /**
   * Swaps Shopify's native result count in the tab title ("Search: 17 results
   * found for …") for the Typesense count, so the tab matches the grid. Only set
   * on the search page, via the `native-count` attribute; the title's wording is
   * localized by Shopify, so just the number is replaced.
   * @param {number} found
   */
  #syncTitleCount(found) {
    const nativeCount = this.getAttribute('native-count');
    if (nativeCount === null || nativeCount === '') return;

    const pattern = new RegExp(`(^|\\D)${nativeCount}(?!\\d)`);
    if (!pattern.test(this.#initialTitle)) return;
    document.title = this.#initialTitle.replace(pattern, `$1${found}`);
  }

  /**
   * Updates pagination: re-arms the infinite-scroll observer, or renders numbered links.
   * @param {import('@theme/typesense-client').TypesenseSearchResult} result
   */
  #updatePagination(result) {
    const totalPages = Math.max(1, Math.ceil(result.found / result.perPage));

    if (this.#infiniteScroll) {
      this.refs.pagination.hidden = true;
      this.#observer?.disconnect();
      if (this.#state.page < totalPages) this.#observer?.observe(this.refs.sentinel);
      return;
    }

    this.#renderNumberedPagination(totalPages);
  }

  /**
   * Loads the next page during infinite scroll.
   */
  #loadNextPage() {
    if (this.#loading) return;
    const totalPages = Math.max(1, Math.ceil(this.#found / this.#perPage));
    if (this.#state.page >= totalPages) return;

    this.#state.page += 1;
    this.#runSearch({ append: true });
  }

  /**
   * Renders the numbered pagination control.
   * @param {number} totalPages
   */
  #renderNumberedPagination(totalPages) {
    const nav = this.refs.pagination;
    if (totalPages <= 1) {
      nav.hidden = true;
      nav.innerHTML = '';
      return;
    }

    const current = this.#state.page;
    const strings = Theme.search?.strings;

    /** @param {number} page @param {boolean} disabled @param {string} label @param {string} content */
    const arrow = (page, disabled, label, content) =>
      `<li class="typesense-pagination__item"><button type="button" class="typesense-pagination__link typesense-pagination__arrow" data-page="${page}"${
        disabled ? ' disabled' : ''
      } aria-label="${escapeHtml(label)}">${content}</button></li>`;

    const numbers = paginationRange(current, totalPages)
      .map((page) =>
        page === ELLIPSIS
          ? `<li class="typesense-pagination__item"><span class="typesense-pagination__ellipsis">…</span></li>`
          : `<li class="typesense-pagination__item"><button type="button" class="typesense-pagination__link${
              page === current ? ' is-current' : ''
            }" data-page="${page}"${page === current ? ' aria-current="page"' : ''}>${page}</button></li>`
      )
      .join('');

    const icons = Theme.search?.icons;
    nav.innerHTML =
      `<ul class="typesense-pagination__list list-unstyled">` +
      arrow(current - 1, current <= 1, strings?.paginationPrevious ?? 'Previous', icons?.chevronLeft ?? '‹') +
      numbers +
      arrow(current + 1, current >= totalPages, strings?.paginationNext ?? 'Next', icons?.chevronRight ?? '›') +
      `</ul>`;
    nav.hidden = false;
  }

  /**
   * Fetches the native product cards for the given hits (in Typesense order) by
   * re-rendering this section with an `id:` search, then injects them into the grid.
   *
   * Reusing the section keeps the merchant-configured `_product-card` block tree,
   * so cards are identical to native Shopify search (prices, quick-add, variants,
   * localization) — Typesense only supplies the ranking.
   * @param {import('@theme/typesense-client').TypesenseProductHit[]} hits
   * @param {boolean} append - Whether to append (load more) or replace the grid.
   * @param {AbortSignal} signal
   * @returns {Promise<number>} How many hits got no card (see `#missing`).
   */
  async #renderNativeCards(hits, append, signal) {
    const { grid } = this.refs;
    const ids = hits.map((hit) => hit.id).filter(Boolean);

    if (!ids.length) {
      if (!append) grid.replaceChildren();
      return 0;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    // The store's search settings may hide sold-out products, which would drop
    // their cards from the bridge response. The ids are explicit — Typesense has
    // already applied the "Show out-of-stock" setting — so always allow them.
    url.searchParams.set('options[unavailable_products]', 'show');

    const html = await sectionRenderer.getSectionHTML(this.#sectionId, false, url);
    if (signal.aborted) return 0;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const itemsById = new Map();
    for (const item of parsed.querySelectorAll('.product-grid__item')) {
      const productId = item.getAttribute('data-product-id');
      if (productId && !itemsById.has(productId)) itemsById.set(productId, item);
    }

    // Shopify's `id:` search ignores Typesense's ranking, so reorder to match it.
    const ordered = ids.map((id) => itemsById.get(String(id))).filter((item) => item instanceof Element);

    if (append) grid.append(...ordered);
    else grid.replaceChildren(...ordered);

    return ids.length - ordered.length;
  }

  /**
   * Renders all configured facet groups.
   * @param {import('@theme/typesense-client').TypesenseFacet[]} facets
   * @param {import('@theme/typesense-client').TypesenseFacet[]} treeFacets - Counts
   *   taken without the hierarchy clauses, so category siblings survive a selection.
   * @param {number} treeFound - Documents `treeFacets` was counted over.
   */
  #renderFacets(facets, treeFacets, treeFound) {
    const strings = Theme.search?.strings;
    const byField = new Map(facets.map((facet) => [facet.field, facet.counts]));
    const treeByField = new Map(treeFacets.map((facet) => [facet.field, facet.counts]));

    const groups = this.#facets.map((facet) => {
      if (facet.depth) return this.#renderHierarchyFacet(facet, treeByField, treeFound);
      if (facet.field === PRICE_FIELD) return this.#renderPriceFacet(facet.label);
      return this.#renderCheckboxFacet(facet, byField.get(facet.field) ?? []);
    });

    this.refs.facetList.innerHTML = this.#renderToggles() + groups.join('');

    if (this.refs.clearAll) {
      const hasActive =
        this.#state.filters.size > 0 ||
        this.#state.toggles.size > 0 ||
        Boolean(this.#state.priceMin || this.#state.priceMax);
      this.refs.clearAll.hidden = !hasActive;
      this.refs.clearAll.textContent = strings?.clearAll ?? '';
    }
  }

  /**
   * Wraps a facet body in a collapsible header (matching the native vertical look).
   *
   * Groups are collapsed by default and reopen only if the shopper expanded them
   * earlier in the session (`#openFacets`), so a re-render keeps them as they were.
   * @param {string} key - The facet field, used to remember the open state.
   * @param {string} label - The facet group label.
   * @param {number} activeCount - The number of active values (shown as a bubble).
   * @param {string} body - The facet body markup.
   * @returns {string}
   */
  #facetShell(key, label, activeCount, body) {
    const caret = Theme.search?.icons.caret ?? '';
    const bubble = activeCount > 0 ? `<span class="typesense-facet__bubble">${activeCount}</span>` : '';
    const open = this.#openFacets.has(key) ? ' open' : '';

    return (
      `<details class="typesense-facet" data-facet="${escapeHtml(key)}"${open}>` +
      `<summary class="typesense-facet__summary">` +
      `<span class="typesense-facet__title">${escapeHtml(label)}</span>` +
      `<span class="typesense-facet__summary-end">${bubble}<span class="typesense-facet__caret svg-wrapper">${caret}</span></span>` +
      `</summary>` +
      `<div class="typesense-facet__body">${body}</div>` +
      `</details>`
    );
  }

  /**
   * Renders the toggle filters, pinned above the facet groups.
   *
   * No counts: a toggle's expression can name any filterable field, which needn't
   * be facetable, so the response carries no count for it.
   * @returns {string}
   */
  #renderToggles() {
    const toggles = this.#toggles;
    if (!toggles.length) return '';

    const items = toggles
      .map((toggle) => {
        const checked = this.#state.toggles.has(toggle.key) ? ' checked' : '';
        return (
          `<li class="typesense-toggle__item">` +
          `<label class="typesense-toggle__option">` +
          `<span class="typesense-toggle__label">${escapeHtml(toggle.label)}</span>` +
          `<input type="checkbox" role="switch" class="typesense-toggle__switch" data-toggle="${escapeHtml(
            toggle.key
          )}"${checked}>` +
          `</label></li>`
        );
      })
      .join('');

    return `<div class="typesense-toggles"><ul class="typesense-toggle__list list-unstyled">${items}</ul></div>`;
  }

  /**
   * @typedef {Object} HierarchyNode
   * @property {string} path - The full path (`Feeding > Bottles`), i.e. the facet value.
   * @property {string} label - The last segment, shown to the shopper.
   * @property {number} count - Matching documents.
   * @property {HierarchyNode[]} children - Child nodes, alphabetical.
   */

  /**
   * Rebuilds the category tree from the flat per-level counts.
   *
   * Levels are walked shallowest first so a node's parent is always in place by
   * the time the node itself arrives. Structure comes from the value: every
   * level indexes the full path, so the parent of `A > B > C` is `A > B`.
   * Orphans — a level indexed without its parent, or a parent lost to the
   * facet-value cap — have nowhere to hang and are dropped.
   * @param {FacetConfig} facet
   * @param {Map<string, import('@theme/typesense-client').TypesenseFacetCount[]>} treeByField
   * @returns {HierarchyNode[]} The root nodes, alphabetical.
   */
  #buildHierarchyTree(facet, treeByField) {
    /** @type {Map<string, HierarchyNode>} */
    const nodes = new Map();
    /** @type {HierarchyNode[]} */
    const roots = [];

    for (const field of hierarchyLevels(facet)) {
      for (const entry of treeByField.get(field) ?? []) {
        const path = entry.value;
        if (!path || nodes.has(path)) continue;

        const segments = path.split(HIERARCHY_SEPARATOR);
        const parent = nodes.get(segments.slice(0, -1).join(HIERARCHY_SEPARATOR));
        if (segments.length > 1 && !parent) continue;

        /** @type {HierarchyNode} */
        const node = {
          path,
          label: (segments[segments.length - 1] ?? '').trim() || path,
          count: entry.count,
          children: [],
        };

        nodes.set(path, node);
        if (parent) parent.children.push(node);
        else roots.push(node);
      }
    }

    /** @param {HierarchyNode[]} level */
    const sortLevel = (level) => {
      level.sort((a, b) => a.label.localeCompare(b.label));
      for (const node of level) sortLevel(node.children);
    };
    sortLevel(roots);

    return roots;
  }

  /**
   * The selected node of a hierarchical facet, deepest level first.
   * @param {FacetConfig} facet
   * @returns {string} The selected path, or '' when the whole tree is showing.
   */
  #hierarchySelection(facet) {
    const levels = hierarchyLevels(facet);
    for (let level = levels.length - 1; level >= 0; level--) {
      const [value] = this.#state.filters.get(levels[level] ?? '') ?? [];
      if (value) return value;
    }
    return '';
  }

  /**
   * Moves a hierarchical facet's selection to `path` (or clears it).
   *
   * The node is stored under the level field matching its own depth, so it
   * reaches the URL and `filter_by` through the same paths as a checkbox value.
   * Only one node is ever selected: a deeper value already implies its parents.
   * @param {FacetConfig} facet
   * @param {string} path - The node to select, or '' to clear.
   */
  #selectHierarchyNode(facet, path) {
    const levels = hierarchyLevels(facet);
    for (const field of levels) this.#state.filters.delete(field);

    if (!path) return;
    const field = levels[pathDepth(path)];
    if (field) this.#state.filters.set(field, new Set([path]));
  }

  /**
   * The node standing for the collection itself, on a collection page.
   *
   * The indexer files a product under every menu branch any of its collections
   * sits in, so a collection's products also carry unrelated departments. The
   * collection's own node is the one holding every result: the deepest such
   * node, as its ancestors hold every result too. Ties (a collection linked at
   * two places in the menu) go to the first one found.
   *
   * Once found, the node sticks for the page load: a brand filter can leave a
   * single subcategory holding every result, which must not become the root.
   * @param {FacetConfig} facet
   * @param {HierarchyNode[]} roots
   * @param {number} total - Documents the tree was counted over.
   * @returns {HierarchyNode | undefined} Undefined off collection pages, or when
   *   the collection isn't in the menu.
   */
  #findCollectionNode(facet, roots, total) {
    if (!this.#baseFilter || total <= 0) return undefined;

    const known = this.#hierarchyRoots.get(facet.field)?.path;
    /** @type {HierarchyNode | undefined} */
    let found;
    /** @param {HierarchyNode[]} level */
    const walk = (level) => {
      for (const node of level) {
        if (known) {
          if (node.path === known) found = node;
          else if (known.startsWith(`${node.path}${HIERARCHY_SEPARATOR}`)) walk(node.children);
          continue;
        }
        if (node.count !== total) continue;
        if (!found || pathDepth(node.path) > pathDepth(found.path)) found = node;
        walk(node.children);
      }
    };
    walk(roots);

    return found;
  }

  /**
   * Renders a hierarchical (drill-down) category menu.
   *
   * On a collection page the tree starts below the collection's own node, so
   * only its subcategories are offered. A top-level node without children
   * (NEW, BEST SELLERS) is a merchandising link rather than a department: its
   * products span the whole store, so the full tree shows, minus that node.
   * @param {FacetConfig} facet
   * @param {Map<string, import('@theme/typesense-client').TypesenseFacetCount[]>} treeByField
   * @param {number} treeFound - Documents the tree was counted over.
   * @returns {string}
   */
  #renderHierarchyFacet(facet, treeByField, treeFound) {
    const tree = this.#buildHierarchyTree(facet, treeByField);
    const collectionNode = this.#findCollectionNode(facet, tree, treeFound);
    // Decided on first sight, while no filter has thinned the node's children.
    if (collectionNode && !this.#hierarchyRoots.has(facet.field)) {
      const reRoot = collectionNode.children.length > 0 || pathDepth(collectionNode.path) > 0;
      this.#hierarchyRoots.set(facet.field, { path: collectionNode.path, reRoot });
    }

    // Search results span the whole store, so the drill-down from departments
    // is noise: offer just the deepest categories, flat.
    if (!this.#baseFilter) return this.#renderLeafFacet(facet, tree);

    const root = this.#hierarchyRoots.get(facet.field);
    /** @type {HierarchyNode[]} */
    let roots = tree;
    if (root?.reRoot) {
      // A known root missing from this response (filters emptied it) hides the
      // facet rather than falling back to every department.
      roots = collectionNode?.children ?? [];
    } else if (root) {
      roots = tree.filter((node) => node.path !== root.path);
    }
    if (!roots.length) return '';

    const selected = this.#hierarchySelection(facet);
    return this.#facetShell(facet.field, facet.label, selected ? 1 : 0, this.#renderHierarchyLevel(roots, facet, selected));
  }

  /**
   * Renders a hierarchical facet as a flat list of its deepest categories (the
   * search page), without the parent departments.
   *
   * A leaf is a node with no children in the current counts. Top-level leaves
   * (NEW, BEST SELLERS) are merchandising links rather than categories, so they
   * are left out. The same category filed under two departments (`MUM >
   * Breastfeeding > Milk Storage` and `FEEDING > Breastfeeding > Milk Storage`)
   * is one entry that selects both paths; only paths of the same depth merge, so
   * every selected path sits in one level field and the filter ORs them. Its
   * count is the larger of the two, as the branches mostly hold the same products.
   * @param {FacetConfig} facet
   * @param {HierarchyNode[]} tree
   * @returns {string}
   */
  #renderLeafFacet(facet, tree) {
    /** @type {Map<string, { label: string, paths: string[], count: number }>} */
    const leaves = new Map();
    /** @param {HierarchyNode[]} level */
    const walk = (level) => {
      for (const node of level) {
        if (node.children.length) {
          walk(node.children);
          continue;
        }
        if (pathDepth(node.path) === 0) continue;

        const key = `${pathDepth(node.path)}|${node.label.toLocaleLowerCase()}`;
        const leaf = leaves.get(key);
        if (leaf) {
          leaf.paths.push(node.path);
          leaf.count = Math.max(leaf.count, node.count);
        } else {
          leaves.set(key, { label: node.label, paths: [node.path], count: node.count });
        }
      }
    };
    walk(tree);
    if (!leaves.size) return '';

    const selected = new Set(hierarchyLevels(facet).flatMap((field) => [...(this.#state.filters.get(field) ?? [])]));
    const sorted = [...leaves.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    const hasShowMore = sorted.length > HIERARCHY_VISIBLE + 1;

    const items = sorted
      .map((leaf, index) => {
        const isSelected = leaf.paths.some((path) => selected.has(path));
        const overflow = hasShowMore && index >= HIERARCHY_VISIBLE && !isSelected;

        return (
          `<li class="typesense-hierarchy__item${overflow ? ' hidden' : ''}"${overflow ? ' data-overflow' : ''}>` +
          `<button type="button" class="typesense-hierarchy__link${isSelected ? ' is-selected' : ''}" ` +
          `data-hierarchy="${escapeHtml(facet.field)}" data-paths="${escapeHtml(JSON.stringify(leaf.paths))}"` +
          `${isSelected ? ' aria-current="true"' : ''}>` +
          `<span class="typesense-hierarchy__value">${escapeHtml(leaf.label)}</span>` +
          `<span class="typesense-facet__count">${leaf.count}</span>` +
          `</button></li>`
        );
      })
      .join('');

    const showMore = hasShowMore
      ? `<li class="typesense-hierarchy__item">` +
        `<button type="button" class="typesense-facet__more" data-hierarchy-more aria-expanded="false">${escapeHtml(
          Theme.search?.strings?.showMore ?? ''
        )}</button></li>`
      : '';

    return this.#facetShell(
      facet.field,
      facet.label,
      selected.size ? 1 : 0,
      `<ul class="typesense-hierarchy__list list-unstyled">${items}${showMore}</ul>`
    );
  }

  /**
   * Renders one level of the category tree, recursing into the selected branch only.
   * @param {HierarchyNode[]} nodes - The nodes at this level.
   * @param {FacetConfig} facet
   * @param {string} selected - The selected path.
   * @param {number} [depth] - The level being rendered (0 = roots).
   * @returns {string}
   */
  #renderHierarchyLevel(nodes, facet, selected, depth = 0) {
    const hasShowMore = nodes.length > HIERARCHY_VISIBLE + 1;

    const items = nodes
      .map((node, index) => {
        const isSelected = node.path === selected;
        const onPath = isSelected || selected.startsWith(`${node.path}${HIERARCHY_SEPARATOR}`);
        // The open branch stays visible wherever it falls in the list.
        const overflow = hasShowMore && index >= HIERARCHY_VISIBLE && !onPath;
        const expandable = node.children.length > 0;

        return (
          `<li class="typesense-hierarchy__item${overflow ? ' hidden' : ''}"${overflow ? ' data-overflow' : ''}>` +
          `<button type="button" class="typesense-hierarchy__link${isSelected ? ' is-selected' : ''}" ` +
          `data-hierarchy="${escapeHtml(facet.field)}" data-path="${escapeHtml(node.path)}"` +
          `${expandable ? ` aria-expanded="${onPath}"` : ''}${isSelected ? ' aria-current="true"' : ''}>` +
          `<span class="typesense-hierarchy__value">${escapeHtml(node.label)}</span>` +
          `<span class="typesense-facet__count">${node.count}</span>` +
          `</button>` +
          (onPath && expandable ? this.#renderHierarchyLevel(node.children, facet, selected, depth + 1) : '') +
          `</li>`
        );
      })
      .join('');

    const strings = Theme.search?.strings;
    const showMore = hasShowMore
      ? `<li class="typesense-hierarchy__item">` +
        `<button type="button" class="typesense-facet__more" data-hierarchy-more aria-expanded="false">${escapeHtml(
          strings?.showMore ?? ''
        )}</button></li>`
      : '';

    return (
      `<ul class="typesense-hierarchy__list${depth ? ' typesense-hierarchy__list--child' : ''} list-unstyled">` +
      items +
      showMore +
      `</ul>`
    );
  }

  /**
   * Renders a checkbox facet group from Typesense counts.
   * @param {FacetConfig} facet
   * @param {import('@theme/typesense-client').TypesenseFacetCount[]} counts
   * @returns {string}
   */
  #renderCheckboxFacet(facet, counts) {
    if (!counts.length) return '';
    if (ALPHABETICAL_FACETS.has(facet.field)) {
      counts = [...counts].sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }));
    }
    const selected = this.#state.filters.get(facet.field) ?? new Set();
    const initialVisible = 10;
    const hasShowMore = counts.length > initialVisible + 1;

    const items = counts
      .map((entry, index) => {
        const checked = selected.has(entry.value) ? ' checked' : '';
        const overflow = hasShowMore && index >= initialVisible;
        return (
          `<li class="typesense-facet__item${overflow ? ' hidden' : ''}"${overflow ? ' data-overflow' : ''}>` +
          `<label class="typesense-facet__option">` +
          `<input type="checkbox" class="typesense-facet__checkbox" name="${escapeHtml(facet.field)}" value="${escapeHtml(
            entry.value
          )}"${checked}>` +
          `<span class="typesense-facet__value">${escapeHtml(entry.value)}</span>` +
          `<span class="typesense-facet__count">${entry.count}</span>` +
          `</label></li>`
        );
      })
      .join('');

    const showMore = hasShowMore
      ? `<button type="button" class="typesense-facet__more" data-show-more aria-expanded="false">${escapeHtml(
          Theme.search?.strings.showMore ?? ''
        )}</button>`
      : '';

    return this.#facetShell(
      facet.field,
      facet.label,
      selected.size,
      `<ul class="typesense-facet__list list-unstyled">${items}</ul>${showMore}`
    );
  }

  /**
   * Renders the price range facet (applies on input blur, like native).
   * @param {string} label
   * @returns {string}
   */
  #renderPriceFacet(label) {
    const search = Theme.search;
    const symbol = escapeHtml(search?.currencySymbol ?? '');
    const separator = escapeHtml(search?.priceSeparator ?? '–');
    const safeLabel = escapeHtml(label);

    /** @param {string} name @param {string} value @param {string} placeholder @param {string} ariaSuffix */
    const field = (name, value, placeholder, ariaSuffix) =>
      `<div class="typesense-facet__price-field">` +
      `<span class="typesense-facet__price-symbol" aria-hidden="true">${symbol}</span>` +
      `<input type="text" inputmode="decimal" class="typesense-facet__price-input" name="${name}" value="${escapeHtml(
        value
      )}" placeholder="${escapeHtml(placeholder)}" autocomplete="off" aria-label="${safeLabel} ${ariaSuffix}">` +
      `</div>`;

    const body =
      `<div class="typesense-facet__price">` +
      field('price-min', this.#state.priceMin, '0', 'min') +
      `<span class="typesense-facet__price-separator">${separator}</span>` +
      field('price-max', this.#state.priceMax, '', 'max') +
      `</div>`;

    const active = Boolean(this.#state.priceMin || this.#state.priceMax);
    return this.#facetShell(PRICE_FIELD, label, active ? 1 : 0, body);
  }

  #renderSortOptions() {
    const checkmark = Theme.search?.icons.checkmark ?? '';
    const options = this.#sortOptions;
    const selectedExpr = this.#state.sort || this.#defaultSort;
    const hasSelection = options.some((option) => option.expr === selectedExpr);

    this.refs.sortPanel.innerHTML = options
      .map((option, index) => {
        const selected = option.expr === selectedExpr;
        // Keep one option keyboard-focusable (roving tabindex): the selected one, else the first.
        const tabbable = selected || (!hasSelection && index === 0);
        return (
          `<button type="button" class="typesense-sort__option" role="option" data-expr="${escapeHtml(option.expr)}" ` +
          `aria-selected="${selected}" tabindex="${tabbable ? '0' : '-1'}">` +
          `<span class="typesense-sort__check svg-wrapper" aria-hidden="true">${checkmark}</span>` +
          `<span class="typesense-sort__option-label">${escapeHtml(option.label)}</span>` +
          `</button>`
        );
      })
      .join('');
  }

  /** @param {Event} event */
  #handleFacetChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.name === 'price-min' || input.name === 'price-max') {
      this.#applyPriceFilter();
      return;
    }

    const toggleKey = input.dataset.toggle;
    if (toggleKey) {
      if (input.checked) this.#state.toggles.add(toggleKey);
      else this.#state.toggles.delete(toggleKey);
      this.#resetAndSearch();
      return;
    }

    if (input.type !== 'checkbox') return;

    const values = this.#state.filters.get(input.name) ?? new Set();
    if (input.checked) values.add(input.value);
    else values.delete(input.value);

    if (values.size) this.#state.filters.set(input.name, values);
    else this.#state.filters.delete(input.name);

    this.#resetAndSearch();
  };

  /**
   * Remembers which facet groups are open so a re-render doesn't collapse them.
   * @param {Event} event
   */
  #handleFacetToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    const key = details.dataset.facet;
    if (!key) return;

    if (details.open) this.#openFacets.add(key);
    else this.#openFacets.delete(key);
  };

  /** @param {Event} event */
  #handleFacetClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const treeMore = event.target.closest('[data-hierarchy-more]');
    if (treeMore instanceof HTMLButtonElement) {
      this.#toggleHierarchyShowMore(treeMore);
      return;
    }

    const node = event.target.closest('[data-hierarchy]');
    if (node instanceof HTMLButtonElement) {
      this.#selectHierarchyFromClick(node);
      return;
    }

    const moreButton = event.target.closest('[data-show-more]');
    if (moreButton instanceof HTMLButtonElement) this.#toggleShowMore(moreButton);
  };

  /**
   * Applies a category node click: selects it, or drills back up to its parent
   * when it was already the selected node.
   * @param {HTMLButtonElement} button
   */
  #selectHierarchyFromClick(button) {
    const facet = this.#hierarchyFacets.find((entry) => entry.field === button.dataset.hierarchy);
    if (!facet) return;

    // A flat leaf entry (search page): toggles its paths, which share a level.
    if (button.dataset.paths) {
      /** @type {string[]} */
      const paths = JSON.parse(button.dataset.paths);
      const field = hierarchyLevels(facet)[pathDepth(paths[0] ?? '')];
      const wasSelected = button.getAttribute('aria-current') === 'true';
      this.#selectHierarchyNode(facet, '');
      if (field && !wasSelected) this.#state.filters.set(field, new Set(paths));
      this.#resetAndSearch();
      return;
    }

    const path = button.dataset.path;
    if (!path) return;

    let next =
      path === this.#hierarchySelection(facet) ? path.split(HIERARCHY_SEPARATOR).slice(0, -1).join(HIERARCHY_SEPARATOR) : path;

    // Drilling up past a re-rooted tree's top level clears the selection: the
    // collection's own node already matches everything on the page.
    const root = this.#hierarchyRoots.get(facet.field);
    if (root?.reRoot && !next.startsWith(`${root.path}${HIERARCHY_SEPARATOR}`)) next = '';

    this.#selectHierarchyNode(facet, next);
    this.#resetAndSearch();
  }

  /**
   * Toggles the overflow nodes of one tree level.
   *
   * Scoped to the button's own list, unlike `#toggleShowMore`: a tree has a
   * separate list — and its own control — at every level.
   * @param {HTMLButtonElement} button
   */
  #toggleHierarchyShowMore(button) {
    const list = button.closest('ul');
    if (!list) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    for (const item of list.querySelectorAll(':scope > [data-overflow]')) item.classList.toggle('hidden', expanded);

    const strings = Theme.search?.strings;
    button.textContent = expanded ? strings?.showMore ?? '' : strings?.showLess ?? '';
  }

  /**
   * Toggles the overflow values of a checkbox facet.
   * @param {HTMLButtonElement} button
   */
  #toggleShowMore(button) {
    const body = button.closest('.typesense-facet__body');
    if (!body) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    for (const item of body.querySelectorAll('[data-overflow]')) item.classList.toggle('hidden', expanded);

    const strings = Theme.search?.strings;
    button.textContent = expanded ? strings?.showMore ?? '' : strings?.showLess ?? '';
  }

  /** @param {KeyboardEvent} event */
  #handlePriceKeydown = (event) => {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.name !== 'price-min' && event.target.name !== 'price-max') return;
    event.preventDefault();
    this.#applyPriceFilter();
  };

  #applyPriceFilter() {
    const min = this.refs.facetList.querySelector('[name="price-min"]');
    const max = this.refs.facetList.querySelector('[name="price-max"]');
    this.#state.priceMin = min instanceof HTMLInputElement ? min.value.trim() : '';
    this.#state.priceMax = max instanceof HTMLInputElement ? max.value.trim() : '';
    this.#resetAndSearch();
  }

  /** @param {Event} event */
  #handleSortSelect = (event) => {
    const option = event.target instanceof HTMLElement ? event.target.closest('[data-expr]') : null;
    if (!(option instanceof HTMLElement)) return;

    this.#state.sort = option.dataset.expr ?? '';
    this.#closeSort();
    this.refs.sortDetails.querySelector('summary')?.focus();
    this.#renderSortOptions();
    this.#resetAndSearch();
  };

  /** Focuses the selected (or first) option when the dropdown opens. */
  #handleSortToggle = () => {
    if (!this.refs.sortDetails.open) return;
    const target =
      this.refs.sortPanel.querySelector('[aria-selected="true"]') ?? this.refs.sortPanel.querySelector('[role="option"]');
    if (target instanceof HTMLElement) target.focus();
  };

  /** @param {KeyboardEvent} event */
  #handleSortKeydown = (event) => {
    if (!this.refs.sortDetails.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeSort();
      this.refs.sortDetails.querySelector('summary')?.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    const options = Array.from(this.refs.sortPanel.querySelectorAll('[role="option"]'));
    const current = options.indexOf(/** @type {Element} */ (document.activeElement));
    const next =
      event.key === 'ArrowDown'
        ? options[Math.min(current + 1, options.length - 1)]
        : options[Math.max(current - 1, 0)];

    for (const option of options) if (option instanceof HTMLElement) option.tabIndex = -1;
    if (next instanceof HTMLElement) {
      next.tabIndex = 0;
      next.focus();
    }
  };

  /** @param {Event} event */
  #handleDocumentClick = (event) => {
    if (!this.refs.sortDetails.open) return;
    if (event.target instanceof Node && !this.refs.sortDetails.contains(event.target)) this.#closeSort();
  };

  #closeSort() {
    this.refs.sortDetails.open = false;
  }

  /** @param {Event} event */
  #handlePaginationClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const button = event.target.closest('[data-page]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const page = Number(button.dataset.page);
    if (!page || page === this.#state.page) return;

    this.#state.page = page;
    this.#updateUrl();
    this.#runSearch({ append: false });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  #handleClearAll = () => {
    this.#state.filters = new Map();
    this.#state.toggles = new Set();
    this.#state.priceMin = '';
    this.#state.priceMax = '';
    this.#resetAndSearch();
  };

  #handlePopState = () => {
    this.#readStateFromUrl();
    this.#renderSortOptions();
    this.#runSearch({ append: false });
  };
}

if (!customElements.get('typesense-search-page')) {
  customElements.define('typesense-search-page', TypesenseSearchPage);
}
